import { DialogComponent } from '@theme/dialog';
import { CartAddEvent, CartUpdateEvent } from '@theme/events';

/**
 * A custom element that manages a cart drawer.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener('dialog:open', this.#handleOpen);
    this.addEventListener('dialog:close', this.#handleClose);
  }

  /** @type {HTMLElement | null} */
  #opener = null;

  // Onthoud waarmee de lade openging (mandje, of de koopknop bij automatisch openen).
  showDialog() {
    const active = document.activeElement;
    this.#opener = active instanceof HTMLElement && active !== document.body && !this.refs.dialog?.contains(active) ? active : null;
    super.showDialog();
  }

  // Focus terug naar dat element; anders naar het mandje van deze lade.
  #handleClose = () => {
    const target = this.#opener?.isConnected ? this.#opener : this.querySelector('[data-testid="cart-drawer-trigger"]');
    if (target instanceof HTMLElement && target.getClientRects().length > 0) target.focus({ preventScroll: true });
    this.#opener = null;
  };

  #handleOpen = () => {
    snTrack('cart_drawer_open', { item_count: snItemCount(this) });
    snTrackUpgradeView(this);
  };

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener('dialog:open', this.#handleOpen);
    this.removeEventListener('dialog:close', this.#handleClose);
  }

  #handleCartAdd = () => {
    if (!this.hasAttribute('auto-open')) return;

    // De header staat twee keer in de pagina (desktop en mobiel), dus ook de lade.
    // Alleen de zichtbare openen: een onzichtbare open modal blokkeert alle klikken.
    const drawers = [...document.querySelectorAll('cart-drawer-component[auto-open]')];
    const target = drawers.find((drawer) => drawer.getClientRects().length > 0) ?? drawers[0];
    if (target === this) this.showDialog();
  };

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }
}

/*
 * Meten (04-11-2026, ontwerp cart drawer): events naar window.dataLayer (GTM) en, als die er is,
 * Shopify.analytics.publish (Customer events / custom pixel). Geen eigen analytics in het thema.
 */
function snTrack(name, data = {}) {
  try {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: name, ...data });
    window.Shopify?.analytics?.publish?.(name, data);
  } catch (e) {
    // Meten mag de winkelwagen nooit breken.
  }
}

/** @param {Element} root */
function snItemCount(root) {
  const el = root.querySelector('[ref="cartItemCount"]');
  return el ? Number(el.textContent.trim()) || 0 : 0;
}

const snUpgradeSeen = new Set();

/** Blok Voordeliger in beeld: één keer per combinatie van/naar per pagina. @param {Element} root */
function snTrackUpgradeView(root) {
  const block = root.querySelector('[data-sn-upgrade]');
  if (!block || block.getClientRects().length === 0) return;
  const key = `${block.dataset.from}>${block.dataset.variant}`;
  if (snUpgradeSeen.has(key)) return;
  snUpgradeSeen.add(key);
  snTrack('cart_upgrade_view', snUpgradeData(block));
}

/** @param {HTMLElement} block */
function snUpgradeData(block) {
  return {
    from_units: Number(block.dataset.fromUnits),
    to_units: Number(block.dataset.toUnits),
    to_variant: block.dataset.variant,
  };
}

/** Sectie-id's van de winkelwagen in de pagina (lade 2x en eventueel /cart), voor de Section Rendering API. */
function snCartSectionIds() {
  return [...new Set(
    [...document.querySelectorAll('cart-items-component')]
      .map((el) => el instanceof HTMLElement && el.dataset.sectionId)
      .filter(Boolean)
  )];
}

/*
 * Blok Voordeliger (04-11-2026): wissel de regels van dit type in één verzoek naar het grotere pakket.
 * /cart/update.js met de oude regels (op line key) op 0 en het pakket (op variant-id) op 1,
 * daarna CartUpdateEvent met de sectie-HTML: lade, verzendbalk, totalen en mandje verversen zonder reload.
 */
if (!window.snCartUpgrade) {
  window.snCartUpgrade = true;
  document.addEventListener('click', async (event) => {
    const target = event.target instanceof Element ? event.target : null;
    const button = target?.closest('.sn-upgrade__btn');
    const block = button?.closest('[data-sn-upgrade]');
    if (!(button instanceof HTMLElement) || !(block instanceof HTMLElement)) return;
    if (button.getAttribute('aria-busy') === 'true') return;
    button.setAttribute('aria-busy', 'true');

    const updates = {};
    for (const key of (block.dataset.from || '').split(',').filter(Boolean)) updates[key] = 0;
    updates[block.dataset.variant] = 1;
    snTrack('cart_upgrade_click', { ...snUpgradeData(block), from_lines: Object.keys(updates).length - 1 });

    try {
      const response = await fetch(`${window.Theme?.routes?.cart_update_url || '/cart/update'}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ updates, sections: snCartSectionIds().join(',') }),
      });
      const data = await response.json();
      if (!response.ok || data.status) throw new Error(data.description || 'cart update');
      document.dispatchEvent(
        new CartUpdateEvent(data, 'sn-cart-upgrade', {
          source: 'sn-cart-upgrade',
          itemCount: data.item_count,
          variantId: block.dataset.variant,
          sections: data.sections,
        })
      );
    } catch (e) {
      // Netwerk- of voorraadfout: de lade blijft zoals hij was, de knop komt terug.
      button.removeAttribute('aria-busy');
    }
  });

  // Afrekenen vanuit de lade.
  document.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target : null;
    const checkout = target?.closest('.cart-drawer #checkout');
    if (checkout) snTrack('cart_checkout_click', { item_count: snItemCount(checkout.closest('.cart-drawer') || document) });
  });

  // Na elke wijziging: nieuw blok Voordeliger in een open lade telt als vertoning.
  document.addEventListener('cart:update', () => {
    requestAnimationFrame(() => {
      const open = [...document.querySelectorAll('.cart-drawer dialog[open]')][0];
      if (open) snTrackUpgradeView(open);
    });
  });
}

/*
 * Upsell in de lade (14-10-2026): "Toevoegen" zonder de lade te verlaten.
 * Eén listener op document (de lade staat 2x in de pagina en wordt na elke wijziging vervangen).
 * Zelfde route en event als sn-pdp.js: CartAddEvent met de sectie-HTML, zodat de lade, de verzendmeter en het mandje verversen.
 */
if (!window.snCartUpsell) {
  window.snCartUpsell = true;
  document.addEventListener('submit', async (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.hasAttribute('data-sn-upsell')) return;
    event.preventDefault();

    const button = form.querySelector('[type="submit"]');
    if (button?.getAttribute('aria-busy') === 'true') return;
    button?.setAttribute('aria-busy', 'true');

    const formData = new FormData(form);
    const sectionIds = snCartSectionIds();
    if (sectionIds.length) formData.append('sections', sectionIds.join(','));

    try {
      const response = await fetch(window.Theme?.routes?.cart_add_url || '/cart/add.js', {
        method: 'POST',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: formData,
      });
      const data = await response.json();
      if (data.status) return;
      snTrack('cart_crosssell_add', {
        product: button?.getAttribute('data-product') || '',
        variant_id: String(formData.get('id')),
      });
      form.dispatchEvent(
        new CartAddEvent({}, String(formData.get('id')), {
          source: 'sn-cart-upsell',
          itemCount: 1,
          productId: String(formData.get('product-id') || ''),
          sections: data.sections,
        })
      );
    } catch (e) {
      // Netwerkfout: de knop komt terug, de bezoeker kan opnieuw klikken.
    } finally {
      button?.removeAttribute('aria-busy');
    }
  });
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
